import React, { useState } from 'react';
import { DOMAIN_CONFIG } from '../config';
import { ShieldCheck, Zap, Copy, Check, ArrowRight, Lock, Award, Globe } from 'lucide-react';

export const Hero: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const copyDomain = () => {
    navigator.clipboard.writeText(DOMAIN_CONFIG.domainName);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden">
      {/* Background ambient radial glow effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-emerald-500/15 via-cyan-500/10 to-transparent blur-3xl pointer-events-none -z-10 rounded-full"></div>
      <div className="absolute top-10 left-10 w-96 h-96 bg-emerald-500/5 blur-[120px] pointer-events-none -z-10"></div>
      <div className="absolute top-40 right-10 w-96 h-96 bg-cyan-500/5 blur-[120px] pointer-events-none -z-10"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Top Badges */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 text-xs sm:text-sm font-medium tracking-wide mb-8 shadow-inner shadow-emerald-500/10">
          <span className="flex h-2 w-2 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="font-mono uppercase font-semibold text-emerald-300">Market Valuation Phase</span>
          <span className="text-emerald-600">•</span>
          <span className="text-slate-300 hidden sm:inline">Non-Binding Expressions of Interest</span>
        </div>

        {/* Main Domain Hero Display Card */}
        <div className="relative inline-block max-w-4xl mx-auto mb-8">
          <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500 rounded-3xl blur-xl opacity-30 group-hover:opacity-60 transition duration-1000 group-hover:duration-200 animate-pulse-slow"></div>
          
          <div className="relative glass-panel rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-14 border border-slate-700/60 shadow-2xl">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-mono mb-2">
              Domain Asset Showcase & Expressions of Interest
            </div>
            
            <div className="flex items-center justify-center gap-2 sm:gap-3 my-3 flex-nowrap py-1">
              <h1 className="text-2xl min-[400px]:text-3xl sm:text-5xl md:text-6xl font-black font-mono tracking-tight text-white select-all whitespace-nowrap">
                service-now<span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">.dev</span>
              </h1>
              
              <button
                onClick={copyDomain}
                className="p-2 sm:p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 text-slate-300 hover:text-white transition-all transform active:scale-95 shadow-sm shrink-0"
                title="Copy full domain"
              >
                {copied ? <Check className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" /> : <Copy className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>

            <p className="mt-6 text-lg sm:text-xl md:text-2xl text-slate-300 max-w-2xl mx-auto font-normal leading-relaxed">
              The definitive digital address for modern <span className="text-emerald-400 font-medium">IT consulting, cloud architecture</span>, and on-demand developer services.
            </p>

            {/* Micro value badges inside hero card */}
            <div className="mt-8 pt-6 border-t border-slate-800/80 grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
              <div className="flex items-center gap-2.5">
                <Globe className="w-4 h-4 text-emerald-400 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">Dedicated TLD</div>
                  <div className="text-xs font-semibold text-slate-200">Native .dev Developer TLD</div>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">Transfer</div>
                  <div className="text-xs font-semibold text-slate-200">100% Escrow Protected</div>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <Zap className="w-4 h-4 text-amber-400 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">Fulfillment</div>
                  <div className="text-xs font-semibold text-slate-200">Instant Auth Code Push</div>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <Award className="w-4 h-4 text-violet-400 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 font-mono">Target Sector</div>
                  <div className="text-xs font-semibold text-slate-200">Enterprise Cloud & IT</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button Group */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
          <button
            onClick={() => scrollToSection('offer-form')}
            className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-slate-950 bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 hover:from-emerald-300 hover:to-cyan-300 transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2.5 group"
          >
            <span>{DOMAIN_CONFIG.enableOfferForm ? 'Submit Acquisition Offer' : 'Contact Domain Owner'}</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>

          <button
            onClick={() => scrollToSection('why-this-domain')}
            className="w-full sm:w-auto px-6 py-4 rounded-xl font-semibold text-slate-200 bg-slate-900/80 hover:bg-slate-800 border border-slate-700/80 hover:border-slate-600 transition-all flex items-center justify-center gap-2"
          >
            <span>Explore Strategic Value</span>
          </button>
        </div>

        {/* Starting Minimum anchor note */}
        <div className="mt-5 text-xs text-slate-400 font-mono flex items-center justify-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-slate-500" />
          <span>Indicative valuation anchor: ${DOMAIN_CONFIG.minimumOfferUsd.toLocaleString()} USD • Non-binding inquiries • Owner reserves right not to sell</span>
        </div>
      </div>
    </section>
  );
};
